// Detailed liquidity breakdown modal — shows exactly how the "liquidity now" number is built.
import { useEffect, useState } from "react";
import { X, Wallet, PiggyBank } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { LiquidityFlow } from "@/components/dashboard/LiquidityFlow";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString();

const LABELS_AR = { income: "دخل شهري", revenue: "إيراد لمرة واحدة", borrow: "اقتراض",
  repay_lend: "رد إقراض", expense: "مصروف", recurring: "أقساط", savings: "ادخار",
  lend: "إقراض", repay_borrow: "رد اقتراض" };
const LABELS_EN = { income: "Income", revenue: "Revenue", borrow: "Borrow", repay_lend: "Repaid to me",
  expense: "Expense", recurring: "Installments", savings: "Savings", lend: "Lend", repay_borrow: "Repay debt" };

export function LiquidityDetails({ open, onClose }) {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/dashboard/liquidity-breakdown").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [open]);
  if (!open) return null;
  const LABELS = lang === "ar" ? LABELS_AR : LABELS_EN;
  const ccy = data?.currency || "AED";
  const byType = data?.by_type || {};

  return (
    <div data-testid="liq-details-modal" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm grid place-items-end sm:place-items-center p-3" onClick={onClose}>
      <div className="w-full sm:max-w-md neu p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-extrabold neu-accent-blue">
            {lang === "ar" ? "تفاصيل السيولة" : "Liquidity details"}
          </h3>
          <button data-testid="liq-details-close" onClick={onClose} className="w-9 h-9 rounded-full neu-sm grid place-items-center active:scale-95">
            <X className="w-4 h-4" />
          </button>
        </div>
        {!data && <p className="text-sm text-slate-500 py-6 text-center">…</p>}
        {data?.error && <p className="text-sm text-red-500 py-6 text-center">{lang === "ar" ? "تعذّر التحميل" : "Failed to load"}</p>}
        {data && !data.error && (
          <>
            <div className="p-3 rounded-2xl neu-inset mb-3 flex items-center gap-2.5 py-3">
              <PiggyBank className={`w-4 h-4 ${data.accounts_total < 0 ? "text-red-600" : "neu-accent-blue"}`} />
              <p className="text-sm flex-1 truncate">{lang === "ar" ? "رصيد كل الحسابات" : "All accounts balance"}</p>
              <span className={`font-display font-extrabold text-sm tabular-nums ${data.accounts_total < 0 ? "text-red-600" : "neu-accent-blue"}`}>{fmt(data.accounts_total)}</span>
            </div>
            <LiquidityFlow direction="in" byType={byType} total={data.in_total}
              labels={{ ...LABELS, _total: lang === "ar" ? "مجموع الداخل" : "Total in" }}
              title={lang === "ar" ? "الداخل ⬆" : "Inflows ⬆"}
              headerClass="text-emerald-700" totalClass="text-emerald-600" sign="+" />
            <LiquidityFlow direction="out" byType={byType} total={data.out_total}
              labels={{ ...LABELS, _total: lang === "ar" ? "مجموع الخارج" : "Total out" }}
              title={lang === "ar" ? "الخارج ⬇" : "Outflows ⬇"}
              headerClass="text-red-700" totalClass="text-red-600" sign="−" />
            <div className="p-4 rounded-2xl neu-inset flex items-center justify-between">
              <span className="text-sm font-bold flex items-center gap-2"><Wallet className="w-4 h-4 neu-accent-blue" />{lang === "ar" ? "الصافي = السيولة" : "Net = Liquidity"}</span>
              <span className={`font-display text-xl font-extrabold tabular-nums ${data.net < 0 ? "text-red-600" : "neu-accent-blue"}`}>{fmt(data.net)} {ccy}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
